import { Injectable, inject } from '@angular/core';
import { ActivationEnd, ActivationStart, GuardsCheckEnd, NavigationEnd, Router, RoutesRecognized } from '@angular/router';

import { clone } from 'lodash-es';
import { BehaviorSubject, Observable, buffer, distinctUntilChanged, filter, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RouterEventsService {
    private router = inject(Router);

    private currentRouteActivationEnd = new BehaviorSubject<ActivationEnd | undefined>(undefined);
    private currentRouteActivationStart = new BehaviorSubject<ActivationStart | undefined>(undefined);
    private currentRouteRoutesRecognized = new BehaviorSubject<RoutesRecognized | undefined>(undefined);
    private recognizedRoutes: string[] = [];

    /**
     *
     * It filters all the ActivationEnd events that occured before NavigationEnd and takes the very first one
     * That's because angular will emit multiple ActivationEnd event for each child route until entire route tree is resolved
     * and NavigationEnd indicates the end as expected
     *
     * @readonly
     * @type {Observable<ActivationEnd>}
     * @memberof RouterEventsService
     */
    get currentActivationEnd(): Observable<ActivationEnd | undefined> {
        return this.currentRouteActivationEnd.asObservable();
    }

    /**
     *
     * It filters all the ActivationStart events that occured before GuardsCheckEnd and takes the very first one
     * That's because angular will emit multiple ActivationStart event for each child route until entire route tree is resolved
     * and GuardsCheckEnd indicates the end as expected
     *
     * @readonly
     * @type {Observable<ActivationStart>}
     * @memberof RouterEventsService
     */
    get currentActivationStart(): Observable<ActivationStart | undefined> {
        return this.currentRouteActivationStart.asObservable();
    }

    /**
     *
     * It filters all the NavigationStart
     *
     * @readonly
     * @type {Observable<ActivationStart>}
     * @memberof RouterEventsService
     */
    get currentRoutesRecognized(): Observable<RoutesRecognized | undefined> {
        return this.currentRouteRoutesRecognized.asObservable();
    }

    get isDirectLink(): boolean {
        return this.recognizedRoutes.length === 1;
    }

    get urlHistory(): string[] {
        return clone(this.recognizedRoutes);
    }

    constructor() {
        this.initCurrentRouteActivationEnd();
        this.initCurrentRouteActivationStart();
        this.initCurrentRouteRoutesRecognized();
    }

    private initCurrentRouteActivationEnd(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof ActivationEnd),
                buffer(this.router.events.pipe(filter((e) => e instanceof NavigationEnd))),
                map((events) => events[0]),
                distinctUntilChanged(),
            )
            .subscribe(this.currentRouteActivationEnd);
    }

    private initCurrentRouteActivationStart(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof ActivationStart),
                buffer(this.router.events.pipe(filter((e) => e instanceof GuardsCheckEnd))),
                map((events) => this.getLastActivationEvent(events)),
                distinctUntilChanged(),
            )
            .subscribe(this.currentRouteActivationStart);
    }

    private initCurrentRouteRoutesRecognized(): void {
        this.router.events
            .pipe(
                filter((event) => event instanceof RoutesRecognized),
                distinctUntilChanged(),
            )
            .subscribe((event) => {
                this.recognizedRoutes.push(event.url);
                this.currentRouteRoutesRecognized.next(event);
            });
    }

    private getLastActivationEvent(events: ActivationStart[]): ActivationStart | undefined {
        if (events.length <= 1) {
            return events[0];
        }

        const lastEvent = events[events.length - 1];

        // skip the last event if it's the initial overlay navigation (outlet is overlay and path is empty)
        // this happens only the first time the app loads (navigation to empty outlet route)
        // but this breaks printing and so we skip the emission here as it's not an important navigation anyway
        // checks if url is empty, because other navigations in overlay outlet could still be valid
        // only the overlay navigations with empty url should be filtered (= initial navigation on app start)
        if (lastEvent.snapshot.outlet === 'overlay' && lastEvent.snapshot.url.length === 0) {
            return events[events.length - 2];
        }

        return lastEvent;
    }
}
